import { useState, useEffect } from 'preact/hooks';
import { useConversation } from '@elevenlabs/react';
import { useUnifiedWidget } from '../providers/UnifiedWidgetProvider';
import { useConfig } from '../providers/ConfigProvider';
import MicIcon from '../icons/Voice';
import CloseIcon from '../icons/Close';
import WarningIcon from '../icons/Warning';
import { ButtonHTMLAttributes, HTMLAttributes, JSX } from 'preact';
import { PoweredBySA } from './PoweredBy';
import { AgentAvatarWaves } from './AgentAvatarWaves';

type ContainerProps = HTMLAttributes<HTMLDivElement> & {
  open: boolean;
}
const Container = ({ open, children, ...props }: ContainerProps) => {
  const { gooberCss } = useUnifiedWidget();
  const { config } = useConfig();
  return (
    <div 
      {...props}
      className={gooberCss({
        position: "absolute",
        bottom: 0,
        right: config.position === "bottom-right" ? '0' : undefined,
        left: config.position === "bottom-left" ? '0' : undefined,
        width: `${config.width}px`,
        maxWidth: open ? "calc(100vw - 30px)" : "0px",
        background: "#ffffff",
        borderRadius: "12px",
        boxShadow: "0 6px 24px rgba(0, 0, 0, 0.16)",
        flexDirection: "column",
        textAlign: "center",
        overflow: "hidden",
        visibility: open ? "visible" : "hidden",
        opacity: open ? 1 : 0,
        transform: open ? "translateY(0) scale(1)" : "translateY(30px) scale(0.7)",
        transition: open
          ? "opacity 0.15s ease-out, transform 0.2s cubic-bezier(0.20, 1.35, 0.22, 1)"
          : "opacity 0.1s ease-in, transform 0.1s ease-in",
        pointerEvents: open ? "auto" : "none",
      })}
    >
      {children}
    </div>
  )  
}

type ContentProps = HTMLAttributes<HTMLDivElement>
const Content = ({ children, ...props }: ContentProps) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <div {...props} className={gooberCss({
      padding: "24px",
      display: "block",
      width: "100%",
      boxSizing: "border-box",
      boxShadow: "0 0 10px rgba(0, 0, 0, 0.1)",
    })}>
      {children}
    </div>
  )
}

type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> & {
  disabled?: boolean;
  backgroundColor?: string;
  color?: string;
}
const Button = ({ disabled, backgroundColor, color, children, ...props }: ButtonProps) => {
  const { gooberCss } = useUnifiedWidget();

  return (
    <button
      {...props}
      className={gooberCss({
        width: "100%",
        padding: "14px 24px",
        border: "none",
        borderRadius: "8px",
        cursor: disabled ? "not-allowed" : "pointer",
        fontWeight: 600,
        fontSize: "14px",
        transition: "all 0.2s ease",
        minWidth: "200px",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        gap: "8px",
        opacity: disabled ? '0.6' : '1',
        background: backgroundColor,
        color: color,
        marginTop: "10px",
      })}
    >
      {children}
    </button>
  )
}

const ErrorMessage = ({children, ...props}: HTMLAttributes<HTMLDivElement>) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <div {...props} className={gooberCss({
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      gap: "8px",
      padding: "8px",
      background: "#fef2f2",
      border: "1px solid #fecaca",
      borderRadius: "8px",
      color: "#dc2626",
      fontSize: "14px",
      width: "100%",
      boxSizing: "border-box",
      marginTop: "5px",
    })}>
      {children}
    </div>
  )
}

type SpinnerProps = HTMLAttributes<HTMLDivElement> & {
  color: string;
}
const Spinner = ({ color, ...props }: SpinnerProps) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <div {...props} className={gooberCss({
      width: "14px",
      height: "14px",
      border: `2px solid ${color}`,
      borderTop: "2px solid transparent",
      borderRadius: "50%",
      animation: "spin 1s linear infinite",
      '@keyframes spin': {
        '0%': { transform: 'rotate(0deg)' },
        '100%': { transform: 'rotate(360deg)' },
      },
    })} />
  )
}

const FormContainer = ({ children, ...props }: HTMLAttributes<HTMLDivElement>) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <div {...props} className={gooberCss({
      width: "100%",
      display: "flex",
      flexDirection: "column",
      gap: "12px",
      marginTop: "12px",
    })}>
      {children}
    </div>
  )
}

type InputProps = JSX.IntrinsicElements['input'] & {
  hasError?: boolean;
}
const Input = ({ hasError, ...props }: InputProps) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <input {...props} className={gooberCss({
      width: "100%",
      padding: "12px",
      border: `1px solid ${hasError ? '#dc2626' : '#d1d5db'}`,
      borderRadius: "6px",
      fontSize: "16px",
      background: "#ffffff",
      boxSizing: "border-box",
      color: "#111827",
      transition: "border-color 0.2s ease",
      '&:focus': {
        outline: "none",
        borderColor: hasError ? '#dc2626' : '#3b82f6',
      },
      '&::placeholder': {
        color: "#9ca3af",
      },
    })} />
  )
}

type HelperTextProps = HTMLAttributes<HTMLParagraphElement> & {
  isError?: boolean;
  color?: string;
}
const HelperText = ({ isError, color, children, ...props }: HelperTextProps) => {
  const { gooberCss } = useUnifiedWidget();
  return (
    <p {...props} className={gooberCss({
      fontSize: "12px",
      margin: 0,
      color: isError ? '#dc2626' : color,
      opacity: isError ? 1 : 0.7,
    })}>
      {children}
    </p>
  )
}

export function VoiceContent() {
  const { 
    open,
    mode,
    error, 
    setError,
    agentInfo,
    agentInfoError,
    agentInfoLoaded: agentStatusLoaded,
  } = useUnifiedWidget();
  const { config } = useConfig();
  const [isConnecting, setIsConnecting] = useState(false);
  const [userEmail, setUserEmail] = useState<string>('');
  const [emailSubmitted, setEmailSubmitted] = useState<boolean>(false);
  const [emailError, setEmailError] = useState<string | null>(null);
  const { gooberCss } = useUnifiedWidget();

  const isVoiceMode = mode === 'voice';
  const showVoice = open && isVoiceMode;
  const elevenlabsAgentId = agentInfo?.elevenlabs_agent_id ?? null;

  // Surface agent info problems once the user is in voice mode
  useEffect(() => {
    if (!isVoiceMode || !agentStatusLoaded || elevenlabsAgentId) return;
    setError(agentInfoError || 'Agent does not have voice capabilities enabled');
  }, [isVoiceMode, agentStatusLoaded, elevenlabsAgentId, agentInfoError]);

  const conversation = useConversation({
    onConnect: () => {
      setIsConnecting(false);
      setError(null);
    },
    onDisconnect: () => {      
      setIsConnecting(false);
    },
    onError: (error) => {
      setError(error);
      setIsConnecting(false);
    }
  });

  const { status, isSpeaking } = conversation;

  const validateEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  const handleEmailSubmit = (e: Event) => {
    e.preventDefault();
    setEmailError(null);

    if (!userEmail.trim()) {
      setEmailError('Please enter your email address');
      return;
    }

    if (!validateEmail(userEmail)) {
      setEmailError('Please enter a valid email address');
      return;
    }

    setEmailSubmitted(true);
  };

  const handleStartConversation = async () => {
    if (!config.agentId) {
      setError('No agent ID provided');
      return;
    }

    if (!elevenlabsAgentId) {
      setError('ElevenLabs agent ID not loaded. Please wait or check agent configuration.');
      return;
    }

    try {
      setIsConnecting(true);
      setError(null);

      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Browser does not support microphone access');
      }

      await conversation.startSession({
        agentId: elevenlabsAgentId,
        dynamicVariables: {
          user_email: userEmail,
        },
      });
            
    } catch (error) {
      console.error('Error starting conversation:', error);
      setError('Failed to start conversation');
      setIsConnecting(false);
    }
  };

  const handleEndConversation = async () => {
    try {
      await conversation.endSession();
      setError(null);
    } catch (error: unknown) {
      const errorMessage = error instanceof Error ? error.message : 'Failed to end conversation';
      setError(errorMessage);
    }
  };

  const isConnected = status === 'connected';

  // Cleanup: end conversation when widget is closed
  useEffect(() => {
    // If the widget was closed and we have an active conversation, end it
    if (!open && isConnected) {
      console.log('Closed with active conversation - ending conversation...');
      try {
        conversation.endSession()
      } catch (error) {
        console.error('Error ending conversation:', error);
      }
      setError(null);
    }
  }, [open, isConnected]);

  return (
    <Container open={showVoice}>
      <Content>
        <AgentAvatarWaves
          avatar={agentInfo?.avatar}
          colour={config.buttonBgColor}
          iconColour={config.buttonTextColor}
          active={isConnected}
          connecting={isConnecting}
          getFrequencyData={isSpeaking ? conversation.getOutputByteFrequencyData : conversation.getInputByteFrequencyData}
        />

        {agentInfo?.agent_name && (
          <h3 className={gooberCss({
            margin: "4px 0 0",
            fontSize: "20px",
            fontWeight: 600,
            textAlign: "center",
          })}>
            {agentInfo.agent_name}
          </h3>
        )}

        <p className={gooberCss({
          margin: "4px 0 0",
          fontSize: "14px",
          textAlign: "center",
          opacity: 0.7,
          minHeight: "20px",
        })}>
          {isConnected
            ? (isSpeaking ? 'Speaking…' : 'Listening…')
            : isConnecting
              ? 'Connecting…'
              : config.voiceSubtitleText}
        </p>

        {config.showEmailCollectionForm && !emailSubmitted ? (
          <FormContainer>
            <form onSubmit={handleEmailSubmit} style={{ width: '100%' }}>
              <Input
                type="email"
                placeholder="you@example.com"
                value={userEmail}
                onInput={(e: any) => {
                  setUserEmail(e.target.value);
                  setEmailError(null);
                }}
                hasError={!!emailError}
                disabled={!agentStatusLoaded || !elevenlabsAgentId}
              />
              {emailError && (
                <HelperText isError>
                  {emailError}
                </HelperText>
              )}
              {(!emailError && config.voiceHelperText) && (
                <HelperText color={config.textColor}>
                  {config.voiceHelperText}
                </HelperText>
              )}
              <Button
                type="submit"
                disabled={!agentStatusLoaded || !elevenlabsAgentId}
                backgroundColor={config.buttonBgColor}
                color={config.buttonTextColor}
              >
                {!agentStatusLoaded ? (
                  <>
                    <Spinner color={config.buttonTextColor} />
                    Loading Agent...
                  </>
                ) : !elevenlabsAgentId ? (
                  <>
                    <WarningIcon width={20} height={20} fill={config.buttonTextColor} /> Agent Not Configured
                  </>
                ) : (
                  "Continue →"
                )}
              </Button>
            </form>
          </FormContainer>
        ) : !isConnected ? (
          <Button
            onClick={handleStartConversation}
            disabled={isConnecting || !agentStatusLoaded || !elevenlabsAgentId}
            backgroundColor={config.buttonBgColor}
            color={config.buttonTextColor}
          >
            {isConnecting ? (
              <>
                <Spinner color={config.buttonTextColor} />
                Connecting...
              </>
            ) : (
              <>
                <MicIcon fill={config.buttonTextColor} width={22} height={22} /> {config.startVoiceConversationText}
              </>
            )}
          </Button>
        ) : (
          <Button backgroundColor={config.buttonBgColor} color={config.buttonTextColor} onClick={handleEndConversation}>
            <CloseIcon fill={config.buttonTextColor} width={20} height={20} /> {config.endVoiceConversationText}
          </Button>
        )}

        {error && (
          <ErrorMessage>
            <WarningIcon width={20} height={20} fill={"#dc2626"} /> {error}
          </ErrorMessage>
        )}
        { !config.removeWatermark && <PoweredBySA /> }
      </Content>
    </Container>
  );
}

